import type { AuditFields } from '@crm/shared';

/**
 * WHATSAPP MESSAGE — Messaggio WhatsApp del CRM, agganciabile a un contatto
 * (Person) tramite Relationship polimorfica. Schema allineato a rest-openapi.yaml
 * (componente `WhatsAppMessage`).
 *
 * Sola lettura + invio: gli inbound nascono dal webhook Meta, gli outbound si
 * creano con l'azione `send` (POST /whatsapp-messages/{groupId}/send). Il
 * "contatto" è implicito via le associazioni (Relationship), non è un campo
 * dell'entità: qui c'è solo `phoneNumber` (numero, testo).
 */
export type WhatsAppMessageDirection = 'IN' | 'OUT';

/**
 * Tipo di contenuto del messaggio. SYNC CROSS-LAYER con l'enum Java
 * `it.dvel.crm.util.constants.WhatsAppMessageType`: se cambi i valori, aggiornali
 * anche là.
 */
export type WhatsAppMessageType =
  | 'TEXT'
  | 'IMAGE'
  | 'AUDIO'
  | 'VIDEO'
  | 'DOCUMENT'
  | 'TEMPLATE';

/**
 * I tipi che portano un binario (Document Library lato server, endpoint
 * /media/content lato API). TEXT e TEMPLATE non ne hanno.
 *
 * Predicato per APPARTENENZA e non per negazione (`type !== 'TEXT'`): quello era un
 * insieme aperto, e infatti quando è arrivato TEMPLATE ci è finito dentro da solo,
 * facendo cercare un media inesistente. Se domani si aggiunge un tipo, o compare qui
 * esplicitamente o non è un media — che è il default giusto.
 */
const WHATSAPP_MEDIA_TYPES: readonly WhatsAppMessageType[] = [
  'IMAGE',
  'AUDIO',
  'VIDEO',
  'DOCUMENT',
];

export function isWhatsAppMediaType(type: WhatsAppMessageType): boolean {
  return WHATSAPP_MEDIA_TYPES.includes(type);
}

/**
 * Solo le cifre del numero, senza un `00` internazionale iniziale: la regola di
 * `PhoneNormalizer.digits` lato server, nella forma in cui il backend salva il
 * numero della sessione. Unica copia lato client (pagina WhatsApp e mock).
 */
export function phoneDigits(phone: string): string {
  return phone.replace(/[^0-9]/g, '').replace(/^00/, '');
}

/**
 * Il numero del contatto da proporre: quello che combacia con `preferredDigits`
 * (il numero di una sessione), altrimenti il principale, altrimenti il primo.
 */
export function defaultWhatsAppPhone(
  phones: readonly { number: string; primary: boolean }[],
  preferredDigits?: string,
): string {
  const preferred = preferredDigits
    ? phones.find((p) => phoneDigits(p.number) === preferredDigits)
    : undefined;
  return (preferred ?? phones.find((p) => p.primary) ?? phones[0])?.number ?? '';
}

/**
 * Nota vocale: registrata col microfono di WhatsApp, non un file audio allegato.
 *
 * Meta manda entrambi come `type: 'AUDIO'` e li distingue con un flag a parte — per
 * questo qui non basta guardare il tipo. È anche il motivo per cui non esiste un
 * `WhatsAppMessageType` 'VOICE': "voice" non è un tipo valido della Graph API, e
 * inventarselo avrebbe rotto l'invio al primo che ci prova.
 *
 * Solo le note vocali si trascrivono: un mp3 allegato può essere musica.
 */
export function isVoiceNote(message: WhatsAppMessage): boolean {
  return message.type === 'AUDIO' && message.voiceNote === true;
}

/**
 * Stato di consegna dei messaggi in uscita, dalle callback di Meta.
 *
 * ACCEPTED non è "inviato": la Graph API risponde 200 appena prende in carico il
 * messaggio, e valuta le policy dopo. È il motivo per cui un messaggio poteva
 * risultare inviato nel CRM e non arrivare mai al contatto.
 *
 * SYNC CROSS-LAYER con l'enum Java
 * `it.dvel.crm.util.constants.WhatsAppMessageStatus` e con le chiavi i18n
 * `whatsapp.status.*` in locales/{it,en}.json.
 */
export type WhatsAppMessageStatus =
  | 'ACCEPTED'
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'PLAYED'
  | 'FAILED';

export interface WhatsAppMessage extends AuditFields {
  whatsAppMessageId: number;
  direction: WhatsAppMessageDirection;
  /** Id Meta del messaggio (wamid). */
  externalId: string;
  /** Numero dell'altra parte (mittente per IN, destinatario per OUT). */
  phoneNumber: string;
  senderName: string;
  /** Numero business che ha ricevuto/inviato. */
  phoneNumberId: string;
  /** Tipo di contenuto: TEXT, media (IMAGE/AUDIO/VIDEO/DOCUMENT) o TEMPLATE. */
  type: WhatsAppMessageType;
  /** Testo del messaggio o, per i media, la didascalia (caption). */
  body: string;
  /** MIME type del media (solo per i messaggi media). */
  mediaMimeType?: string;
  /** Nome file del media (solo per i messaggi media). */
  mediaFileName?: string;
  /**
   * Nota vocale e non file audio allegato. Vedi {@link isVoiceNote}: da leggere sempre
   * col predicato, mai da solo, perché ha senso unicamente insieme a `type === 'AUDIO'`.
   *
   * False anche sulle note vocali ricevute prima di TS-77: il flag non veniva salvato e
   * Meta non lo ripropone. Restano file audio agli occhi della UI.
   */
  voiceNote?: boolean;
  receiptDate: string;
  /** Stato di consegna: solo sui messaggi OUT, assente sugli IN. */
  status?: WhatsAppMessageStatus;
  statusDate?: string;
  /** Codice errore Meta sui FAILED (es. 131047 = finestra 24h scaduta); 0 se nessuno. */
  errorCode?: number;
  errorMessage?: string;
  /** Testo della nota vocale. Assente finché nessuno preme Trascrivi. */
  transcription?: string;
  /** Riassunto: facoltativo, il motore può non produrlo anche a trascrizione riuscita. */
  transcriptionSummary?: string;
  /** Azioni emerse, una per riga. Facoltativo come il riassunto. */
  transcriptionActionItems?: string;
  /**
   * Quando la trascrizione è stata ottenuta. Assente = mai trascritto: è questo il flag
   * su cui la UI decide se mostrare il tasto o il testo — non `transcription`, che
   * potrebbe teoricamente essere una stringa vuota per un audio senza parole.
   */
  transcriptionDate?: string;
}

/**
 * Stato della finestra di 24 ore di Meta su una sessione: decide cosa si può
 * inviare al contatto. Da non confondere col raggruppamento dei messaggi in
 * accordion, che è una scelta di presentazione nostra.
 *
 * SYNC CROSS-LAYER con l'enum Java
 * `it.dvel.crm.util.constants.WhatsAppWindowState` e con le chiavi i18n
 * `whatsapp.window.*` in locales/{it,en}.json.
 */
export type WhatsAppWindowState = 'OPEN' | 'PENDING' | 'CLOSED';

/** Corpo del 409 quando si tenta di scrivere a finestra chiusa. */
export interface WhatsAppWindowConflict {
  windowState: WhatsAppWindowState;
  windowExpirationDate?: string;
  message: string;
  /**
   * Template di ricontatto configurato, se c'è ed è approvato. Presente = la UI lo
   * preseleziona e chiede solo conferma, mostrando il testo che partirà davvero.
   * Assente = la UI fa scegliere fra i template approvati.
   */
  template?: WhatsAppTemplate;
}

/** Modello di messaggio approvato da Meta: l'unica cosa inviabile a finestra chiusa. */
export interface WhatsAppTemplate {
  name: string;
  language: string;
  category: string;
  /** Testo del corpo coi segnaposto ({{1}}, {{2}}, ...) ancora dentro. */
  body: string;
  parameterCount: number;
}

/**
 * Numero WhatsApp business mittente configurato per la company (TS-89). Popola la
 * combo di scelta del mittente. Allineato allo schema `WhatsAppNumber` del
 * rest-openapi.yaml.
 */
export interface WhatsAppNumber {
  whatsAppNumberId: number;
  /** Id Meta del numero: è il valore inviato come mittente (`phoneNumberId`). */
  phoneNumberId: string;
  /** Numero in formato E.164 leggibile. */
  phoneNumber: string;
  /** Etichetta assegnata dall'amministratore, mostrata nella combo. */
  label: string;
}

/**
 * Payload per l'invio di un messaggio outbound a un contatto.
 *
 * `phoneNumberId` è il numero business mittente scelto: assente = il backend usa il
 * numero predefinito della company (dopo il cutover di TS-89).
 */
export interface WhatsAppMessageSendPayload {
  personId: number;
  phoneNumber: string;
  body: string;
  phoneNumberId?: string;
}

/** Payload per l'invio di un media (file) a un contatto; la caption riusa il testo. */
export interface WhatsAppMediaSendPayload {
  personId: number;
  phoneNumber: string;
  caption?: string;
  file: File;
  phoneNumberId?: string;
}

/** Payload per l'invio di un template approvato. */
export interface WhatsAppTemplateSendPayload {
  personId: number;
  phoneNumber: string;
  templateName: string;
  language: string;
  parameters: string[];
  phoneNumberId?: string;
}

/**
 * Errore di invio. Porta il conflitto di finestra quando il backend risponde 409,
 * così il composer può proporre un template invece di mostrare un errore generico.
 */
export interface WhatsAppSendError {
  message: string;
  windowConflict?: WhatsAppWindowConflict;
}
